import { useEffect } from 'react';
import { z } from 'zod';

import { isPlainRecord } from '@/utils/object';

export const DIRECTORY_DEFAULT_PAGE_SIZE = 25;
export const DIRECTORY_MAX_PAGE_SIZE = 100;

export const directoryListSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  limit: z.coerce.number().int().optional().catch(undefined),
  offset: z.coerce.number().int().optional().catch(undefined),
});

export type DirectoryListSearch = z.infer<typeof directoryListSearchSchema>;

export type ParsedDirectoryListSearch = {
  search: string;
  page: number;
  pageSize: number;
};

export function directoryListParams(search: string, page: number, pageSize: number) {
  return {
    search: search.trim() || undefined,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  };
}

export function parseDirectoryListSearch(params: DirectoryListSearch): ParsedDirectoryListSearch {
  const pageSize =
    params.limit === undefined || !Number.isFinite(params.limit)
      ? DIRECTORY_DEFAULT_PAGE_SIZE
      : Math.min(DIRECTORY_MAX_PAGE_SIZE, Math.max(1, params.limit));
  const offset =
    params.offset === undefined || !Number.isFinite(params.offset) || params.offset < 0 ? 0 : params.offset;
  const page = Math.floor(offset / pageSize) + 1;

  return {
    search: params.search?.trim() ?? '',
    page,
    pageSize,
  };
}

export function serializeDirectoryListSearch({
  search,
  page,
  pageSize,
}: ParsedDirectoryListSearch): DirectoryListSearch {
  return {
    search: search.trim() || undefined,
    limit: pageSize === DIRECTORY_DEFAULT_PAGE_SIZE ? undefined : pageSize,
    offset: page <= 1 ? undefined : (page - 1) * pageSize,
  };
}

type UseDirectoryPaginationArgs = {
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  totalCount: number;
  isLoading: boolean;
};

/**
 * Keeps `placeholderData` only when the previous directory query used the same
 * `search`. Reads the last query-key segment so an Orval path change does not
 * silently keep or drop the placeholder.
 */
export const keepDirectoryPlaceholder = <T>(
  previousData: T | undefined,
  previousQuery: { queryKey: readonly unknown[] } | undefined,
  trimmedSearch: string | undefined,
): T | undefined => {
  const previousParams = previousQuery?.queryKey.at(-1);
  if (!isPlainRecord(previousParams)) return undefined;
  return previousParams.search === trimmedSearch ? previousData : undefined;
};

export const useDirectoryPagination = ({
  page,
  setPage,
  pageSize,
  setPageSize,
  totalCount,
  isLoading,
}: UseDirectoryPaginationArgs): { currentPage: number; onPageSizeChange: (nextSize: number) => void } => {
  const lastPage = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = totalCount === 0 ? page : Math.min(page, lastPage);

  useEffect(() => {
    if (isLoading || totalCount === 0 || page <= lastPage) return;
    setPage(lastPage);
  }, [isLoading, totalCount, page, lastPage, setPage]);

  return { currentPage, onPageSizeChange: setPageSize };
};
